/**
 * ChurchesDirectory — grouped browsing: Country → Province/District → Assembly.
 * HQ, country offices and provinces are navigation groupings, so they never
 * appear as church cards and never count towards the congregation total.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  MapPin, Phone, Mail, ExternalLink, ArrowRight, ArrowLeft, Search,
  Church, Clock, UserRound, Globe2,
} from 'lucide-react';
import FwmShell from '@/components/fwm/FwmShell';
import { FwmHero, FwmStatStrip } from '@/components/fwm/FwmHero';
import { useHero } from '@/components/fwm/useHero';
import { FWM_IMAGERY, resolveImage } from '@/components/fwm/imagery';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Crumbs = ({ trail, onJump }) => (
  <nav className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--fwm-muted)]"
       data-testid="churches-breadcrumbs">
    {trail.map((step, i) => (
      <span key={step.label} className="flex items-center gap-2">
        {i > 0 && <span>/</span>}
        {i < trail.length - 1 ? (
          <button onClick={() => onJump(i)} className="hover:text-[color:var(--fwm-ink)] transition-colors"
                  data-testid={`churches-crumb-${i}`}>
            {step.label}
          </button>
        ) : (
          <span className="text-[color:var(--fwm-ink)] font-medium">{step.label}</span>
        )}
      </span>
    ))}
  </nav>
);

const Tile = ({ title, subtitle, count, unit, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
          className="fwm-card group text-left p-7 flex items-start justify-between gap-6">
    <span className="min-w-0">
      <span className="fwm-display block text-xl leading-snug">{title}</span>
      {subtitle && <span className="mt-1.5 block text-xs text-[color:var(--fwm-muted)]">{subtitle}</span>}
      <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--fwm-accent-dark)]">
        Browse <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </span>
    <span className="text-right shrink-0">
      <span className="fwm-display block text-3xl tabular-nums">{count}</span>
      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--fwm-muted)]">
        {unit}
      </span>
    </span>
  </button>
);

const LEVEL_LABELS = {
  campus: 'Campus',
  bible_college_campus: 'Bible College Campus',
  home_group: 'Home group',
  ministry: 'Ministry',
  cell: 'Cell group',
};

const AssemblyCard = ({ church }) => {
  const logo = resolveImage(church.logo_url);
  // iter 636 — a logo that fails to load used to leave a broken-image icon in
  // the card; fall through to the same placeholder a church with no logo gets.
  const [logoBroken, setLogoBroken] = useState(false);
  return (
    <article className="fwm-card p-7" data-testid={`church-${church.id}`}>
      <div className="flex items-start gap-4">
        {logo && !logoBroken ? (
          <img src={logo} alt={church.name} className="h-12 w-12 object-contain"
               loading="lazy" onError={() => setLogoBroken(true)} />
        ) : (
          <span className="h-12 w-12 grid place-items-center border border-[color:var(--fwm-line)] rounded-[3px]">
            <Church className="h-5 w-5 text-[color:var(--fwm-accent)]" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="fwm-display text-xl leading-snug">{church.name}</h3>
          <p className="mt-1 text-xs text-[color:var(--fwm-muted)]">
            {[church.city, church.country].filter(Boolean).join(', ')}
          </p>
        </div>
        {church.level !== 'assembly' && (
          <span className="fwm-badge" data-testid={`church-level-${church.id}`}>
            {LEVEL_LABELS[church.level] || String(church.level).replace(/_/g, ' ')}
          </span>
        )}
      </div>

      {church.description && (
        <p className="mt-5 text-sm text-[color:var(--fwm-body)] line-clamp-3">{church.description}</p>
      )}

      <dl className="mt-6 space-y-2.5 text-sm text-[color:var(--fwm-body)]">
        {church.pastor && (
          <div className="flex items-start gap-2.5">
            <UserRound className="h-4 w-4 mt-0.5 text-[color:var(--fwm-muted)] shrink-0" />
            <span>{church.pastor_title ? `${church.pastor_title} ` : ''}{church.pastor}</span>
          </div>
        )}
        {church.address && (
          <div className="flex items-start gap-2.5">
            <MapPin className="h-4 w-4 mt-0.5 text-[color:var(--fwm-muted)] shrink-0" />
            <span>{church.address}</span>
          </div>
        )}
        {church.contact_phone && (
          <div className="flex items-start gap-2.5">
            <Phone className="h-4 w-4 mt-0.5 text-[color:var(--fwm-muted)] shrink-0" />
            <a href={`tel:${church.contact_phone}`} className="hover:text-[color:var(--fwm-ink)]">
              {church.contact_phone}
            </a>
          </div>
        )}
        {church.contact_email && (
          <div className="flex items-start gap-2.5">
            <Mail className="h-4 w-4 mt-0.5 text-[color:var(--fwm-muted)] shrink-0" />
            <a href={`mailto:${church.contact_email}`} className="truncate hover:text-[color:var(--fwm-ink)]">
              {church.contact_email}
            </a>
          </div>
        )}
        {church.service_times?.length > 0 ? (
          <div className="flex items-start gap-2.5">
            <Clock className="h-4 w-4 mt-0.5 text-[color:var(--fwm-muted)] shrink-0" />
            <span>
              {church.service_times.map((s) => `${s.name}: ${[s.day, s.time].filter(Boolean).join(' ')}`).join(' · ')}
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-2.5 text-[color:var(--fwm-muted)]">
            <Clock className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Service times available from the assembly</span>
          </div>
        )}
      </dl>

      <div className="mt-7 flex flex-wrap gap-3">
        <Link to={church.public_url} className="fwm-btn" data-testid={`church-visit-${church.id}`}>
          Visit this house <ArrowRight className="h-4 w-4" />
        </Link>
        {church.website_url && (
          <a href={church.website_url} target="_blank" rel="noopener noreferrer" className="fwm-btn-ghost">
            <ExternalLink className="h-4 w-4" /> Website
          </a>
        )}
      </div>
    </article>
  );
};

const ChurchesDirectory = () => {
  const [country, setCountry] = useState(null);
  const [group, setGroup] = useState(null);
  const [q, setQ] = useState('');

  const hero = useHero('churches', { image_url: FWM_IMAGERY.congregation || FWM_IMAGERY.choir });
  const watchBand = useHero('churches_watch', { image_url: FWM_IMAGERY.churchBuilding });

  const { data, isLoading } = useQuery({
    queryKey: ['churches-browse'],
    queryFn: () => fetch(`${API_URL}/api/churches/browse`).then((r) => r.json()),
    staleTime: 300000,
  });

  const countries = data?.countries || [];
  const countryNode = countries.find((c) => c.name === country) || null;
  const groupNode = countryNode?.groups.find((g) => g.name === group) || null;

  // Search runs across every level and jumps straight to matching assemblies
  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return null;
    const hits = [];
    countries.forEach((c) => {
      const countryHit = c.name.toLowerCase().includes(term);
      c.groups.forEach((g) => {
        const groupHit = g.name.toLowerCase().includes(term);
        g.assemblies.forEach((a) => {
          const own = [a.name, a.city, a.address, a.pastor].filter(Boolean)
            .some((v) => v.toLowerCase().includes(term));
          if (own || countryHit || groupHit) hits.push(a);
        });
      });
    });
    return hits;
  }, [q, countries]);

  const trail = [
    { label: 'All countries', reset: () => { setCountry(null); setGroup(null); } },
    ...(countryNode ? [{ label: countryNode.name, reset: () => setGroup(null) }] : []),
    ...(groupNode ? [{ label: groupNode.name, reset: () => {} }] : []),
  ];

  return (
    <FwmShell ctaLabel="Find your church" ctaLink="/churches" testId="churches-directory" overDark>
      <FwmHero
        testId="churches-hero"
        image={hero.image_url}
        fallbackImage={hero.fallback_image_url}
        video={hero.video_url}
        mediaPriority={hero.media_priority}
        eyebrow={hero.eyebrow}
        title={hero.title}
        accent={hero.accent}
        blurb={hero.blurb}
        actions={(
          <button className="fwm-btn fwm-btn-lg" data-testid="churches-find-btn"
                  onClick={() => document.getElementById('directory')
                    ?.scrollIntoView({ behavior: 'smooth' })}>
            Find an assembly <ArrowRight className="h-4 w-4" />
          </button>
        )}
      />

      <FwmStatStrip testId="churches-stats" stats={[
        [isLoading ? '—' : (data?.total_assemblies ?? 0), 'Assemblies'],
        [data?.country_count ?? 0, 'Countries'],
        [countries.length || '—', 'Regions listed'],
        [data?.total_provinces
          || countries.reduce((sum, c) => sum + (c.groups?.length || 0), 0)
          || '—', 'Provinces & districts'],
      ]} />

      {/* Affiliated works live in their own directory */}
      <section className="mx-auto max-w-[1180px] px-6 lg:px-10 mt-16">
        <Link to="/affiliated-ministries" className="fwm-card block p-6 group"
              data-testid="churches-to-affiliated">
          <span className="fwm-kicker">Not part of the FWM network</span>
          <p className="mt-2 text-sm leading-relaxed text-[color:var(--fwm-muted)]">
            Independent churches and ministries under the Archbishop's covering are listed
            separately and are not counted above.{' '}
            <span className="font-semibold text-[color:var(--fwm-accent-dark)] inline-flex items-center gap-1.5">
              Affiliated churches &amp; ministries
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </p>
        </Link>
      </section>

      {/* Search + country filter */}
      <section className="border-y border-[color:var(--fwm-line)] bg-[color:var(--fwm-ground-2)] mt-20"
               id="directory">        <div className="mx-auto max-w-[1180px] px-6 lg:px-10 py-6 flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-0 sm:min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--fwm-muted)]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="churches-search"
                   placeholder="Search a house, city, province or pastor"
                   className="pl-10 rounded-[3px] bg-[color:var(--fwm-card)] border-[color:var(--fwm-line)]" />
          </div>
          <Select value={country || 'all'}
                  onValueChange={(v) => { setCountry(v === 'all' ? null : v); setGroup(null); setQ(''); }}>
            <SelectTrigger className="w-full max-w-[240px] rounded-[3px] bg-[color:var(--fwm-card)] border-[color:var(--fwm-line)]"
                           data-testid="churches-country-filter">
              <SelectValue placeholder="All countries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All countries</SelectItem>
              {countries.map((c) => (
                <SelectItem key={c.name} value={c.name} data-testid={`churches-country-option-${c.name}`}>
                  {c.name} · {c.congregation_count}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="mx-auto max-w-[1180px] px-6 lg:px-10 py-16">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-40" />)}
          </div>
        ) : matches ? (
          /* ── Search results jump straight to assemblies ── */
          <div data-testid="churches-search-results">
            <div className="flex items-baseline justify-between gap-4 mb-8">
              <h2 className="fwm-display text-3xl tracking-tight">
                {matches.length} match{matches.length !== 1 ? 'es' : ''} for “{q.trim()}”
              </h2>
              <button onClick={() => setQ('')} className="text-xs font-semibold text-[color:var(--fwm-accent-dark)]"
                      data-testid="churches-clear-search">
                Clear search
              </button>
            </div>
            {matches.length === 0 ? (
              <p className="text-sm text-[color:var(--fwm-muted)]" data-testid="churches-no-matches">
                Nothing matched that. Try a city, a country or part of the assembly name.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {matches.map((a) => <AssemblyCard key={a.id} church={a} />)}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <Crumbs trail={trail} onJump={(i) => trail[i].reset()} />
              {(countryNode || groupNode) && (
                <button data-testid="churches-back"
                        onClick={() => (groupNode ? setGroup(null) : setCountry(null))}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--fwm-accent-dark)]">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
              )}
            </div>

            {/* Level 1 — countries */}
            {!countryNode && (
              <div data-testid="churches-country-level">
                <h2 className="fwm-display text-3xl tracking-tight mb-8">Browse by country</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {countries.map((c) => (
                    <Tile key={c.name} title={c.name}
                          subtitle={c.office ? c.office.name : 'Assemblies and campuses'}
                          count={c.congregation_count} unit="Congregations"
                          testId={`churches-country-${c.name}`}
                          onClick={() => { setCountry(c.name); setGroup(null); }} />
                  ))}
                </div>
                {countries.length === 0 && (
                  <p className="text-sm text-[color:var(--fwm-muted)]" data-testid="churches-empty">
                    The directory is being prepared.
                  </p>
                )}
              </div>
            )}

            {/* Level 2 — provinces / districts */}
            {countryNode && !groupNode && (
              <div data-testid="churches-group-level">
                <h2 className="fwm-display text-3xl tracking-tight">{countryNode.name}</h2>
                {countryNode.office && (
                  <p className="mt-3 flex items-center gap-2 text-sm text-[color:var(--fwm-muted)]"
                     data-testid="churches-country-office">
                    <Globe2 className="h-4 w-4" /> National office: {countryNode.office.name}
                    {countryNode.office.city ? `, ${countryNode.office.city}` : ''}
                  </p>
                )}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {countryNode.groups.map((g) => (
                    <Tile key={g.name} title={g.name} subtitle={String(g.level).replace('_', ' ')}
                          count={g.congregation_count} unit="Congregations"
                          testId={`churches-group-${g.name}`}
                          onClick={() => setGroup(g.name)} />
                  ))}
                </div>
                {countryNode.groups.length === 0 && (
                  <p className="mt-8 text-sm text-[color:var(--fwm-muted)]"
                     data-testid="churches-country-empty">
                    No assembly is listed here yet — reach the national office above and they will
                    point you to the nearest house.
                  </p>
                )}
              </div>
            )}

            {/* Level 3 — assemblies */}
            {groupNode && (
              <div data-testid="churches-assembly-level">
                <h2 className="fwm-display text-3xl tracking-tight mb-8">
                  {groupNode.name} · {countryNode.name}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {groupNode.assemblies.map((a) => <AssemblyCard key={a.id} church={a} />)}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Watch band */}
      <section className="border-t border-[color:var(--fwm-line)]">
        <div className="mx-auto max-w-[1180px] px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <img src={watchBand.image_url} alt="A Faith World Ministries assembly"
               data-testid="churches-watch-image"
               onError={watchBand.onImageError}
               className="lg:col-span-6 w-full h-[240px] lg:h-[320px] object-cover" loading="lazy" />
          <div className="lg:col-span-6">
            <h2 className="fwm-display text-3xl sm:text-4xl tracking-tight">
              Can't get to a house this week?
            </h2>
            <p className="mt-4 text-[color:var(--fwm-body)]">
              Join the service live on Faith Works TV, or catch the teaching on demand.
            </p>
            <Link to="/tv" className="fwm-btn fwm-btn-lg mt-8 inline-flex" data-testid="churches-tv-cta">
              Watch Faith Works TV <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </FwmShell>
  );
};

export default ChurchesDirectory;
